import { Link } from "react-router-dom";
import CodeBlock from "@/components/common/CodeBlock";
import { ArrowRight, FileDown, Terminal, Webhook, Shield, FlaskConical, BookOpen } from "lucide-react";

export default function DocsHome() {
  return (
    <div className="max-w-4xl mx-auto p-10" data-testid="docs-home">
      <div className="mb-3 text-xs uppercase tracking-widest text-[var(--gold)]">Developer Portal · v1.0.5</div>
      <h1 className="text-5xl font-bold tracking-tight leading-tight max-w-3xl">
        Integrate <span className="text-[var(--gold)]">once</span>. Access multiple gaming providers through a single API.
      </h1>
      <p className="text-[var(--text-2)] mt-5 max-w-2xl text-base leading-relaxed">
        CoreBridge Technologies is the B2B middleware between casino operators and game providers. We hold the
        wallet, normalize provider protocols, and stream every event to your webhook endpoint —
        idempotent, signed, and audited.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/docs/quick-start" className="btn-gold px-5 py-2.5 rounded text-sm flex items-center gap-2" data-testid="docs-quickstart-btn">
          Quick start <ArrowRight size={14}/>
        </Link>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/postman-collection`} target="_blank" rel="noreferrer"
           className="btn-ghost px-5 py-2.5 rounded text-sm flex items-center gap-2" data-testid="docs-postman-btn">
          <FileDown size={14}/> Postman collection
        </a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/docs`} target="_blank" rel="noreferrer"
           className="btn-ghost px-5 py-2.5 rounded text-sm flex items-center gap-2" data-testid="docs-swagger-btn">
          OpenAPI explorer
        </a>
      </div>

      <h2 className="text-2xl font-semibold mt-16 mb-4">Architecture</h2>
      <div className="card-surface p-6 font-mono text-sm leading-loose">
        <div>Casino Operator (Your Platform)</div>
        <div className="text-[var(--gold)]">    ↕ <span className="text-[var(--text-3)] text-xs">seamless wallet callbacks + API</span></div>
        <div>CoreBridge Nexus Aggregator <span className="text-[var(--text-3)]">(operator credit float)</span></div>
        <div className="text-[var(--gold)]">    ↓</div>
        <div>Pragmatic Play · TITI Gaming · GG/Lynx · Platipus · SA Gaming · BetGames</div>
      </div>

      <h2 className="text-2xl font-semibold mt-12 mb-4">In 5 minutes</h2>
      <CodeBlock lang="bash" code={`# 1. Login as admin (or use operator API key)
curl -X POST $BASE/api/v1/operator/player/create \\
  -H "X-API-Key: $API_KEY" \\
  -d '{"external_id":"alice_42","currency":"USD"}'

# 2. Launch a game
curl -X POST $BASE/api/v1/operator/game/launch \\
  -H "X-API-Key: $API_KEY" \\
  -d '{"player_id":"ply_xxx","game_id":"gm_0001"}'

# 3. Provider debits the player wallet via aggregator
curl -X POST $BASE/api/v1/wallet/debit \\
  -H "Idempotency-Key: bet_abc123" \\
  -d '{"operator_id":"op_acme","player_id":"ply_xxx","amount":10,"round_id":"rnd_001"}'`}/>

      <h2 className="text-2xl font-semibold mt-12 mb-4">Capabilities</h2>
      <div className="grid grid-cols-2 gap-4">
        {[
          { icon: Terminal, title: "Unified Wallet API", desc: "One ledger across every provider. Idempotent debit / credit / rollback." },
          { icon: Webhook, title: "Real-time Webhooks", desc: "BET · WIN · ROLLBACK · REFUND · BALANCE_CHANGE — signed, retried, replayable." },
          { icon: Shield, title: "HMAC + IP Whitelist", desc: "SHA-256 signatures, timestamp validation, replay protection." },
          { icon: FlaskConical, title: "Sandbox", desc: "Simulate every flow — duplicate callbacks, provider timeouts, insufficient funds." },
          { icon: BookOpen, title: "OpenAPI explorer", desc: "Try requests live. SDKs in PHP, NodeJS, Python, Java, C#." },
          { icon: ArrowRight, title: "Reconciliation & Settlement", desc: "Provider/Aggregator/Operator triple-match, GGR/NGR splits, CSV exports." },
        ].map((c, i) => (
          <div key={i} className="card-surface p-5">
            <c.icon size={18} className="text-[var(--gold)]"/>
            <div className="font-semibold mt-2">{c.title}</div>
            <div className="text-sm text-[var(--text-2)] mt-1">{c.desc}</div>
          </div>
        ))}
      </div>

      <div className="mt-12 card-surface p-6 border-l-4 border-l-[var(--gold)]">
        <div className="text-xs uppercase tracking-widest text-[var(--gold)] mb-1">Support</div>
        <div className="text-sm">Integration questions? <span className="font-mono">integrations@corebridge.net</span> · 24/7 ops escalation: <span className="font-mono">ops@corebridge.net</span></div>
      </div>
    </div>
  );
}
